import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { BookingRequest } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { Button, Chip, Field, Modal, useToast } from '@/ui'
import { rejectBooking } from './api'
import { REJECT_REASON_LABEL, REJECT_REASONS } from './review'
import styles from './RejectBookingModal.module.css'

interface Props {
  booking: BookingRequest
  onClose: () => void
  onRejected?: () => void
}

/** Reason chip (required) + optional message; both reach the resident as "{motivo} — {mensagem}". */
export function RejectBookingModal({ booking: b, onClose, onRejected }: Props) {
  const { condoId } = useAccess()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const trimmed = message.trim()

  async function reject() {
    if (!reason) return
    setSubmitting(true)
    try {
      await rejectBooking(condoId, b.id, { reason, message: trimmed })
      await queryClient.invalidateQueries({ queryKey: ['bookings', condoId] })
      toast(`Solicitação de ${b.area} recusada`, { icon: 'block' })
      ;(onRejected ?? onClose)()
    } catch {
      toast('Não foi possível recusar a reserva. Tente novamente.', { icon: 'error' })
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Recusar reserva"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Voltar
          </Button>
          <Button
            variant="danger"
            icon="block"
            disabled={!reason}
            loading={submitting}
            onClick={reject}
          >
            Recusar
          </Button>
        </>
      }
    >
      <div className={styles.lead}>
        {b.area} · {b.resident}, {b.unit}. O morador recebe o motivo junto com a notificação.
      </div>
      <div className={styles.group} role="group" aria-label="Motivo">
        <span className={styles.label}>Motivo</span>
        <div className={styles.chips}>
          {REJECT_REASONS.map((r) => (
            <Chip key={r.code} selected={reason === r.code} onClick={() => setReason(r.code)}>
              {r.label}
            </Chip>
          ))}
        </div>
      </div>
      <Field
        label="Mensagem ao morador"
        multiline
        value={message}
        onChange={setMessage}
        placeholder="Opcional"
      />
      {reason && (
        <div className={styles.preview} aria-live="polite">
          O morador recebe: {REJECT_REASON_LABEL[reason] ?? reason}
          {trimmed && ` — ${trimmed}`}
        </div>
      )}
    </Modal>
  )
}
